import React, { useState } from "react";
import { ArrowRight, Bell } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { PRE_HARVEST_MODE } from "@/lib/siteMode";
import WaitlistForm from "../WaitlistForm";

export default function Gift() {
  const { t } = useLang();
  const g = t("gift");
  const [showWaitlist, setShowWaitlist] = useState(false);

  return (
    <section
      id="regalo"
      data-testid="gift-section"
      className="relative bg-cream-200 py-24 sm:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        {/* Text */}
        <div>
          <span className="text-[11px] uppercase tracking-[0.32em] text-gold-deep font-medium">
            {g.overline}
          </span>
          <h2 className="mt-6 font-serif text-5xl lg:text-6xl text-olive-900 font-medium leading-[0.98]">
            {g.title_a} <span className="italic">{g.title_b}</span>
            <br />
            {g.title_c}
          </h2>
          <p className="mt-8 text-olive-800/80 font-light leading-relaxed">{g.body}</p>

          {/* Reference note for pre-harvest */}
          {PRE_HARVEST_MODE && (
            <p
              data-testid="gift-reference-note"
              className="mt-6 text-xs text-olive-800/60 border-l-2 border-gold-deep pl-3 italic"
            >
              {t("pre_harvest.reference_note")}
            </p>
          )}

          {/* Includes list */}
          {g.includes && g.includes.length > 0 && (
            <ul className="mt-8 space-y-2">
              {g.includes.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-olive-800/85">
                  <span className="mt-1 text-gold-deep text-xs shrink-0">—</span>
                  {item}
                </li>
              ))}
            </ul>
          )}

          {/* Price / CTA */}
          <div className="mt-10">
            {PRE_HARVEST_MODE ? (
              <>
                {/* Pre-harvest badge */}
                <div
                  data-testid="gift-preharvest-badge"
                  className="inline-block text-[11px] uppercase tracking-[0.24em] bg-stone-700 text-cream-100 px-3 py-1 mb-5"
                >
                  {t("pre_harvest.product_badge")}
                </div>
                <br />
                <button
                  type="button"
                  data-testid="gift-section-waitlist-btn"
                  onClick={() => setShowWaitlist(true)}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.26em] hover:bg-olive-800 transition-colors"
                >
                  <Bell size={14} strokeWidth={1.4} />
                  {t("pre_harvest.cta_waitlist")}
                </button>
                <p className="mt-3 text-xs text-olive-800/60">
                  {t("pre_harvest.trust_line")}
                </p>
              </>
            ) : (
              <>
                {g.price && (
                  <div className="flex items-baseline gap-3 mb-2">
                    <span className="font-serif text-4xl text-olive-900">{g.price}</span>
                    {g.price_unit && (
                      <span className="text-sm text-olive-800/60">{g.price_unit}</span>
                    )}
                  </div>
                )}
                {g.shipping_badge && (
                  <p className="mb-6 text-xs text-gold-deep uppercase tracking-[0.2em]">
                    {g.shipping_badge}
                  </p>
                )}
                <a
                  href="/producto/estuche-madera-2x500ml"
                  data-testid="gift-section-cta"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.26em] hover:bg-olive-800 transition-colors group"
                >
                  {g.cta}
                  <ArrowRight
                    size={14}
                    strokeWidth={1.4}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </a>
              </>
            )}
          </div>
        </div>

        {/* Image */}
        <div className="relative flex justify-center order-first lg:order-last">
          <img
            src="https://customer-assets.emergentagent.com/job_aceite-granada/artifacts/8nyyb6g4_Estuche%20CSM%202%20botellas%20web.webp"
            alt={g.alt || "Estuche regalo Cortijo Santa María 2 × 500 ml"}
            className="max-h-[520px] w-auto object-contain"
          />
        </div>
      </div>
      {showWaitlist && (
        <WaitlistForm
          onClose={() => setShowWaitlist(false)}
          preselectedProduct="estuche-madera-2x500ml"
        />
      )}
    </section>
  );
}
